/**
 * English messages: the source of every key. ICU MessageFormat syntax
 * (https://unicode-org.github.io/icu/userguide/format_parse/messages/).
 * Keys are `area.name`; add the same key to every other locale.
 */
export const en = {
  "app.name": "Vault Chat",
  "app.skipToContent": "Skip to content",

  "nav.label": "Main",
  "nav.open": "Open navigation",
  "nav.close": "Close navigation",
  "nav.chats": "Chats",
  "nav.notes": "Notebook",
  "nav.registers": "Data",
  "nav.saved": "Saved",
  "nav.settings": "Settings",

  "chats.title": "Chats",
  "chats.empty": "No chats yet. Start one to talk to your assistant.",
  "chats.new": "New chat",
  "chats.search": "Search chats",
  "chats.searchEmpty": "No chats match.",
  "chats.pinned": "Pinned",
  "chats.recent": "Recent",
  "chats.archive": "Archive",
  "chats.archive.open": "Open the archive",
  "chats.archive.close": "Back to chats",
  "chats.archive.empty": "The archive is empty.",
  "chats.more": "Show more",
  "chats.loading": "Loading chats…",
  "chats.loadFailed": "Chats could not be loaded.",
  "chats.untitled": "Untitled chat",
  "chats.unreadableTitle": "Title cannot be shown",
  "chats.running": "Replying",
  "chats.actions": "Actions for {title}",
  "chats.handle": "Move {title}",
  "chats.action.rename": "Rename",
  "chats.action.pin": "Pin",
  "chats.action.unpin": "Unpin",
  "chats.action.moveUp": "Move up",
  "chats.action.moveDown": "Move down",
  "chats.action.archive": "Archive",
  "chats.action.unarchive": "Restore from the archive",
  "chats.rename.title": "Rename chat",
  "chats.rename.label": "Title",
  "chats.delete.title": "Delete this chat?",
  "chats.delete.text":
    "The chat and all its messages are deleted from this server. This cannot be undone. Backups the operator keeps may hold the encrypted copy for a while, and the provider has already seen what it answered.",
  "chat.notFound": "This chat does not exist or was deleted.",
  "chat.newIntro": "Write the first message to start a chat.",
  "chat.messages": "Messages",
  "chat.composer.label": "Message",
  "chat.composer.placeholder": "Write a message",
  "chat.send": "Send",
  "files.attach": "Attach files",
  "files.attached": "Attached files",
  "files.keep": "Keep in this chat",
  "files.notKept":
    "Deleted after the reply, or in about an hour if there is none; the storage may hold the encrypted copy a day or two longer. A description written by the agent stays. The AI provider may keep its own copy.",
  "files.kept": "kept",
  "files.settings": "File settings",
  "files.remove": "Remove",
  "files.preparing": "Preparing…",
  "files.refused.unsupported": "{name}: the agents cannot read files of this type.",
  "files.refused.tooLarge": "{name} is too large: a file can be at most 50 MB, a text file 200 KB.",
  "files.refused.pixels": "{name} has too many pixels: an image can have at most 50 megapixels.",
  "files.refused.pages": "{name} has too many pages: a PDF can have at most 100.",
  "files.refused.notText": "{name} is not a text file.",
  "files.refused.unreadable": "{name} could not be read.",
  "files.refused.count":
    "{count, plural, one {A message carries at most # file.} other {A message carries at most # files.}}",
  "files.big.text":
    "The original keeps its full size and uses more of your storage. The agent reads a compressed copy.",
  "files.big.asIs": "Send as is too",
  "files.open": "Open {name}",
  "files.saveAction": "Save",
  "files.opening": "Opening…",
  "files.saveImage": "Save",
  "files.saveOriginal": "Save original",
  "files.error.quota": "Your files have used up their storage.",
  "files.error.disabled": "This server does not store files.",
  "files.error.tooLarge": "The file is too large.",
  "files.error.limit": "Too many files wait to be sent. Send or remove some first.",
  "files.error.gone": "This file was not kept, or is no longer on the server.",
  "notice.file.undescribed": "The model could not describe a file. It opens the file again when a question needs it.",
  "chat.stop": "Stop the reply",
  "chat.agent.none": "Add a provider in Settings to start chatting.",
  "chat.agent.starting": "Connecting to the provider…",
  "chat.agent.refused": "The server refused the active provider. Check it in Settings.",
  "chat.agent.failed": "The provider could not be connected. Check your connection.",
  "chat.settings.title": "Chat settings",
  "chat.settings.model": "Model",
  "chat.settings.modelDefault": "Default: {name} · {model}",
  "chat.settings.modelNone": "No provider",
  "chat.settings.effort": "Reasoning effort",
  "chat.settings.effortDefault": "The model's default",
  "chat.settings.effort.low": "Low",
  "chat.settings.effort.medium": "Medium",
  "chat.settings.effort.high": "High",
  "chat.settings.effortNote": "Not every model takes an effort; one that does not refuses the reply.",
  "chat.settings.history": "History sent with a message",
  "chat.settings.historyRecent": "{count, plural, one {The last # message} other {The last # messages}}",
  "chat.settings.historyNote":
    "More history tells the model more about the conversation and makes every reply cost more.",
  "chat.settings.historyNone": "No history",
  "chat.settings.historyNoneShort": "no history",
  "chat.settings.historyNoneNote":
    "The model gets only the message being sent and cannot read earlier messages or files of this chat. The chat's memory still goes with it. For one-off requests, like the weather or adding an entry.",
  "chat.settings.messages": "{count, plural, one {# message} other {# messages}}",
  "chat.settings.default": "Default: {choice}",
  "chat.settings.historyCompact": "A summary and the messages after it",
  "chat.settings.compact": "Auto-compaction",
  "chat.settings.compactOff": "Off",
  "chat.settings.compactAt": "At about {count}k tokens",
  "chat.settings.compactAuto": "A summary past about {count}k tokens",
  "chat.settings.compactNote":
    "Past the threshold, earlier messages fold into a summary before a message is sent, and the latest ones go whole. No message is deleted.",
  "chat.settings.compactNow": "Compact now",
  "chat.settings.memory": "Chat memory",
  "chat.settings.memoryNone": "The model has saved nothing for this chat.",
  "chat.settings.memoryUnreadable": "This memory could not be read, so messages cannot be sent until it is cleared.",
  "chat.settings.memoryNote":
    "The model writes these notes itself, with no dialog, and gets them in every turn of this chat. Text it read on a page or in a file could influence them, so clear them if something looks wrong.",
  "chat.settings.memoryClear": "Clear memory",
  "chat.settings.memoryClearFailed": "The memory could not be cleared.",
  "chat.settings.compactShort": "Compact at {count}k",
  "chat.settings.compactNone": "No auto-compaction",
  "chat.context.ofLimit": "History in the next turn: about {used} of {limit} tokens before compaction",
  "chat.context.lastReply": "Last reply by the provider's count: {input} tokens in, {output} out",
  "chat.mention.note": "Note",
  "chat.mention.metric": "Your data",
  "chat.mention.goes": "What you mention goes to {host} with this message.",
  "chat.mention.goesUnknown": "What you mention goes to the provider with this message.",
  "chat.context.used": "History in the next turn: about {used} tokens",
  "chat.temporary.label": "Temporary chat",
  "chat.temporary.note":
    "Deleted from this server about 30 minutes after you leave, backups aside. What the AI provider was sent follows its own rules.",
  "chat.command.list": "Commands",
  "chat.command.compact": "Fold the chat into a summary",
  "chat.command.new": "Start a new chat",
  "chat.command.settings": "Open this chat's settings",
  "chat.author.user": "You",
  "chat.author.assistant": "Assistant",
  "chat.author.tool": "Tool",
  "chat.author.summary": "Summary",
  "chat.streaming": "Writing a reply",
  "chat.edited": "Edited",
  "chat.deleted": "Deleted",
  "chat.cancelled": "Stopped",
  "chat.summary.label": "Summary of the messages above",
  "chat.rich.note": "Note",
  "chat.rich.tip": "Tip",
  "chat.rich.important": "Important",
  "chat.rich.warning": "Warning",
  "chat.rich.caution": "Caution",
  "chat.rich.copyTable": "Copy table as CSV",
  "chat.rich.taskDone": "Done",
  "chat.rich.taskOpen": "Not done",
  "chat.rich.tableCopied": "Table copied as CSV",
  "chat.rich.copyFailed": "Could not copy the table",
  "chat.rich.chart": "Chart",
  "chat.rich.showData": "Show as a table",
  "chat.rich.showChart": "Show as a chart",
  "chat.summary.writing": "Writing a summary",
  "chat.summary.deleted": "Summary deleted",
  "chat.message.actions": "Message actions",
  "chat.message.copy": "Copy text",
  "chat.message.copied": "Text copied",
  "chat.message.copyFailed": "Could not copy the text",
  "chat.message.share": "Share",
  "chat.message.shareFailed": "Could not share the text",
  "chat.message.edit": "Edit",
  "chat.message.save": "Save",
  "chat.message.unsave": "Remove from saved",
  "chat.edit.title": "Edit message",
  "chat.edit.label": "Message",
  "chat.edit.note": "The reply is not written again; later messages see the new text.",
  "chat.delete.title": "Delete this message?",
  "chat.delete.text":
    "Its text is removed from this server and a mark stays in its place. Backups the operator keeps may hold the encrypted copy for a while, and the provider has already seen it.",
  "chat.unreadable.decrypt": "This message cannot be decrypted.",
  "chat.unreadable.parse": "This message cannot be read.",
  "chat.unreadable.version": "This message is in a newer format. Update the app to read it.",
  "chat.unreadable.size": "This message is too large to show.",
  "chat.error.busy": "Wait until the reply is finished, or stop it.",
  "chat.error.turnLimit": "Replies are being written in three other chats. Wait until one of them is finished.",
  "chat.error.tooLarge": "The message is too long.",
  "chat.error.noAgent": "No provider is connected. Check Settings.",
  "chat.error.notFound": "This chat no longer exists.",
  "chat.error.notRetryable": "This reply can no longer be retried.",
  "chat.error.streaming": "Wait until the reply is finished.",
  "chat.error.messageGone": "This message was deleted.",
  "chat.saved.mark": "Saved",
  "chat.saved.added": "Message saved",
  "chat.saved.removed": "Removed from saved",
  "turn.error.provider.blocked": "This server does not allow the provider's address. Check the provider in Settings.",
  "turn.error.provider.unreachable": "The provider could not be reached.",
  "turn.error.provider.refused": "The provider refused the request. Check the API key and the model in Settings.",
  "turn.error.provider.invalid": "The provider sent a reply that could not be read.",
  "turn.error.turn.too_long": "The reply was too long and was cut off.",
  "turn.error.turn.timed_out": "The reply took more than 10 minutes and was stopped.",
  "turn.error.turn.interrupted": "The reply was interrupted.",
  "turn.error.files.busy": "The server is busy with other files. Try again in a minute.",
  "turn.error.generic": "The reply failed.",
  "turn.error.unknown": "The reply failed ({code}).",
  "notes.title": "Notebook",
  "notes.empty": "No notes yet. Notes are encrypted; only you can read them.",
  "notes.pick": "Open a note, or start a new one.",
  "notes.new": "New note",
  "notes.newTitle": "New note",
  "notes.untitled": "Untitled",
  "notes.unreadableTitle": "Unreadable note",
  "notes.search": "Search notes",
  "notes.searchEmpty": "No notes match.",
  "notes.loading": "Loading notes…",
  "notes.loadFailed": "Notes could not be loaded.",
  "notes.more": "Show more",
  "notes.back": "Back to notes",
  "notes.opening": "Opening the note…",
  "notes.openFailed": "The note could not be opened.",
  "notes.titleLabel": "Title",
  "notes.readOnly":
    "This note was written by a newer version of the app or cannot be read, so it cannot be edited here.",
  "notes.unreadableBody": "This note's text cannot be shown by this version of the app.",
  "notes.copyTitle": "{title} (copy {time})",
  "notes.conflict": "The note was changed on another device. Your version was saved as a copy.",
  "notes.deletedElsewhere": "The note was deleted on another device.",
  "notes.delete": "Delete note",
  "notes.deleteTitle": "Delete this note?",
  "notes.deleteText": "The note is deleted on all your devices. This cannot be undone.",
  "notes.status.saving": "Saving…",
  "notes.status.saved": "Saved",
  "notes.status.failed": "Not saved.",
  "notes.status.tooLarge": "The note is too long to save. Shorten it to keep your changes.",
  "notes.editor.area": "Note text",
  "notes.editor.placeholder": "Start writing…",
  "notes.editor.toolbar": "Formatting",
  "notes.editor.heading": "Heading {level}",
  "notes.editor.bold": "Bold",
  "notes.editor.italic": "Italic",
  "notes.editor.strike": "Strikethrough",
  "notes.editor.code": "Code",
  "notes.editor.link": "Link",
  "notes.editor.bulletList": "Bulleted list",
  "notes.editor.orderedList": "Numbered list",
  "notes.editor.taskList": "Task list",
  "notes.editor.quote": "Quote",
  "notes.editor.callout": "Callout",
  "notes.editor.panel": "Collapsible section",
  "notes.editor.codeBlock": "Code block",
  "notes.editor.table": "Table",
  "notes.editor.rule": "Divider",
  "notes.editor.undo": "Undo",
  "notes.editor.redo": "Redo",
  "notes.editor.apply": "Apply",
  "notes.editor.remove": "Remove",
  "notes.editor.linkUrl": "Address",
  "notes.editor.linkInvalid": "Enter an http, https or mailto address.",
  "notes.editor.addRow": "Add a row",
  "notes.editor.addColumn": "Add a column",
  "notes.editor.deleteRow": "Delete the row",
  "notes.editor.deleteColumn": "Delete the column",
  "notes.editor.deleteTable": "Delete the table",
  "registers.title": "Data",
  "registers.empty": "Track weight, steps and more. Values are encrypted; only you can read them.",
  "registers.pick": "Pick a metric to see its entries and chart.",
  "registers.loading": "Loading…",
  "registers.loadFailed": "The metrics could not be loaded.",
  "registers.noEntries": "No entries",
  "registers.latest": "{value} · {day}",
  "registers.back": "Back to data",
  "registers.add": "Add an entry",
  "registers.addTitle": "New entry: {metric}",
  "registers.editTitle": "Edit entry: {metric}",
  "registers.value": "Value",
  "registers.valueIn": "Value, {unit}",
  "registers.when": "Date and time",
  "registers.comment": "Comment",
  "registers.commentHint": "Context for you and the assistant, such as “fasting” or “after a workout”.",
  "registers.save": "Save",
  "registers.edit": "Edit the entry",
  "registers.delete": "Delete the entry",
  "registers.deleteTitle": "Delete this entry?",
  "registers.deleteText": "The entry is deleted on all your devices. This cannot be undone.",
  "registers.view": "Show",
  "registers.view.days": "By day",
  "registers.view.entries": "Every entry",
  "registers.aggregate.avg": "Average of the day",
  "registers.aggregate.sum": "Total of the day",
  "registers.dayEntries": "{count, plural, one {# entry} other {# entries}}",
  "registers.sets": "{count, plural, one {# set} other {# sets}}",
  "registers.best": "best {value}",
  "registers.bestSet": "Best set",
  "registers.chart": "Chart: {metric}",
  "registers.more": "Show earlier entries",
  "registers.shown": "The newest {count} entries are shown.",
  "registers.entriesEmpty": "No entries yet. Add the first one.",
  "registers.entriesLoading": "Loading entries…",
  "registers.entriesFailed": "The entries could not be loaded.",
  "registers.unreadable": "Unreadable entry",
  "registers.invalid.number": "Enter a number.",
  "registers.invalid.integer": "Enter a whole number.",
  "registers.invalid.precision": "Use at most {places} decimal places.",
  "registers.invalid.range": "Enter a value from {min} to {max}.",
  "registers.invalid.when": "Enter a date and time.",
  "registers.conflict": "The entry was changed on another device; its version is shown.",
  "registers.error.limit": "Your account holds the most entries it can.",
  "registers.unit.kg": "kg",
  "registers.unit.cm": "cm",
  "registers.unit.kcal": "kcal",
  "registers.unit.s": "s",
  "saved.title": "Saved messages",
  "saved.empty": "Messages you save appear here.",
  "saved.loading": "Loading saved messages…",
  "saved.loadFailed": "Saved messages could not be loaded.",
  "saved.more": "Show more",
  "saved.open": "Open in chat",
  "saved.remove": "Remove from saved",
  "saved.error.limit": "You can save at most 10,000 messages.",
  "permissions.title": "Allow the agent to read your data?",
  "permissions.intro": "In the chat “{chat}” the agent asks to read:",
  "permissions.introNoChat": "The agent asks to read:",
  "permissions.session": "Time zone, country and language of this device",
  "permissions.history": "Earlier messages of the chat, up to {count}",
  "permissions.web": "A site",
  "permissions.webTitle": "Allow the agent to open this site?",
  "permissions.webIntro": "In the chat “{chat}” the agent asks to open:",
  "permissions.webIntroNoChat": "The agent asks to open:",
  "permissions.webWarning":
    "The site sees this address and the server's IP. An address can carry data out, so check that it holds nothing private. Allowing covers this site until the reply ends.",
  "permissions.weather": "Your place, for the weather",
  "permissions.weatherTitle": "Use this place for the weather?",
  "permissions.weatherIntro": "In the chat “{chat}” the agent asks for the weather where your IP points to:",
  "permissions.weatherIntroNoChat": "The agent asks for the weather where your IP points to:",
  "permissions.weatherWarning":
    "Found by your IP, so it can be another city, especially on mobile data or a VPN. The model learns this place and the weather service gets its rounded coordinates, not your IP. If it is wrong, deny and name your city.",
  "permissions.file": "A file of the chat",
  "permissions.days.all": "All entries",
  "permissions.days.range": "{from} – {to}",
  "permissions.days.from": "From {from}",
  "permissions.days.to": "Until {to}",
  "permissions.reason": "The agent's reason, in its own words",
  "permissions.noReason": "The agent gave no reason.",
  "permissions.unreadableReason": "The reason could not be decrypted.",
  "permissions.host":
    "If you allow it, this data goes to the server and on to {host}, which writes the reply and may keep what it gets. The chat stores the reply, which may quote the data, but not the data itself.",
  "permissions.noHost":
    "The provider of this chat is no longer in your settings, so it is unclear where the data would go. Deny the request.",
  "permissions.approve": "Allow",
  "permissions.addTitle": "Add this entry to your data?",
  "permissions.addIntro": "In the chat “{chat}” the agent proposes to add:",
  "permissions.addIntroNoChat": "The agent proposes to add:",
  "permissions.addLabel": "Entry the agent proposes",
  "permissions.addApprove": "Add",
  "permissions.addSameDay": "This day already has entries: {count}. Adding one may duplicate them.",
  "permissions.addFailedTitle": "The entry was not saved",
  "permissions.addFailed":
    "You approved it and the agent was told it is saved, but saving failed. Try again, or add it yourself in Data.",
  "permissions.addRetry": "Try again",
  "permissions.deny": "Deny",
  "permissions.close": "Close and deny",
  "permissions.failed": "The answer was not sent. Check the connection and try again.",
  "permissions.more":
    "{count, plural, one {One more request waits after this one.} other {# more requests wait after this one.}}",
  "tools.label": "Data the agent asked for",
  "tools.list_metrics": "The list of measurements",
  "tools.read_register": "{metric}, {days}",
  "tools.session_context": "Time zone, country and language",
  "tools.read_history": "Earlier messages of the chat",
  "tools.read_history_count": "Earlier messages of the chat: {count}",
  "tools.read_file": "A file of the chat",
  "tools.set_goal": "Proposed goal: {metric}",
  "permissions.goalTitle": "Set this goal?",
  "permissions.goalIntro": "The agent proposes this goal for your data:",
  "permissions.goalLabel": "Goal the agent proposes",
  "permissions.goalApprove": "Set",
  "permissions.goalRemove": "Remove the goal",
  "registers.goal.open": "Goal",
  "registers.goal.title": "Goal: {metric}",
  "registers.goal.remove": "Remove",
  "registers.goal.targetLabel": "Value to reach",
  "registers.goal.targetIn": "Value to reach, {unit}",
  "registers.goal.line": "Goal",
  "registers.goal.path": "Expected path",
  "registers.goal.target": "Goal: {value}",
  "registers.goal.expected": "expected today: {value}",
  "registers.goal.since": "Path starts on",
  "registers.goal.until": "Reach it by (optional)",
  "registers.goal.untilInvalid": "Pick a day after the start",
  "registers.goal.sinceInvalid": "Pick a day",
  "registers.goal.by": "by {day}",
  "registers.goal.from": "from {value} on {day}",
  "registers.goal.fromDay": "from {day}",
  "registers.goal.overdue": "the day has passed",
  "registers.goal.targetRequired": "Enter the value to reach",
  "tools.add_measurement": "Proposed entry: {metric}",
  "tools.web_fetch": "Site {host}",
  "tools.get_weather": "The weather",
  "tools.write_memory": "Updated the chat's memory",
  "tools.run_code": "A calculation",
  "tools.outcome.running": "running",
  "tools.other": "Tool {tool}",
  "tools.outcome.approved": "allowed",
  "tools.outcome.denied": "denied",
  "tools.outcome.refused": "not available",
  "tools.outcome.timeout": "no answer",
  "tools.outcome.cancelled": "cancelled",
  "tools.outcome.waiting": "waiting for your answer",
  "tools.outcome.reading": "reading",
  "settings.access": "Agent access",
  "settings.access.note": "Every request of the agent to read your data, and how it ended.",
  "settings.access.locked": "Unlock to see the agent's requests.",
  "settings.access.empty": "The agent has not asked for any data yet.",
  "settings.access.failed": "Could not load the agent's requests.",
  "settings.access.more": "Show more",
  "settings.access.reason": "Reason",
  "settings.access.chat": "Chat “{chat}”",
  "settings.access.outcome.approved": "Allowed",
  "settings.access.outcome.denied": "Denied",
  "settings.access.outcome.refused": "Not available on the device",
  "settings.access.outcome.timeout": "Not answered in time",
  "settings.access.outcome.cancelled": "Cancelled with the reply",
  "settings.access.outcome.waiting": "Waiting for an answer",
  "settings.access.outcome.answered": "Read without asking",

  "settings.back": "Back to settings",
  "settings.general": "General",
  "settings.general.hint": "Theme, language and chat defaults",
  "settings.providers.hint": "Models and API keys",
  "settings.keys.hint": "Passkeys, recovery key and this device",
  "settings.account": "Account",
  "settings.account.hint": "Sign-in methods, signing out and deletion",
  "settings.sessions.hint": "Where this account is signed in",
  "settings.activity.hint": "Sign-ins and changes to the account",
  "settings.access.hint": "What the agent asked to read",
  "settings.title": "Settings",
  "settings.appearance": "Appearance",
  "settings.chats": "Chats",
  "settings.chats.note": "Defaults for chats that do not choose their own in the chat settings.",
  "settings.chats.locked": "Unlock to change the chat defaults.",
  "settings.chats.saveFailed": "The chat defaults could not be saved. Try again.",
  "settings.theme": "Theme",
  "settings.theme.system": "System",
  "settings.theme.light": "Light",
  "settings.theme.dark": "Dark",
  "settings.theme.amoled": "AMOLED (black)",
  "settings.language": "Language",

  "signIn.title": "Sign in",
  "signIn.intro":
    "Signing in opens your account. Your chats and notes stay locked until you unlock them with your passkey or recovery key.",
  "signIn.google": "Continue with Google",
  "signIn.email.title": "Sign in with email",
  "signIn.email.label": "Email address",
  "signIn.email.send": "Send code",
  "signIn.code.label": "Code from the email",
  "signIn.code.hint": "We sent an 8-digit code to {email}. It is valid for 10 minutes.",
  "signIn.code.submit": "Sign in",
  "signIn.code.other": "Use another address",
  "signIn.code.resend": "Send a new code",
  "signIn.none": "This server has no sign-in method set up. Ask its administrator.",
  "signIn.loading": "Loading…",
  "signIn.error.cancelled": "Sign-in with Google was cancelled.",
  "signIn.error.failed": "Sign-in with Google did not complete. Try again.",
  "signIn.error.unavailable": "Google cannot be reached right now. Try again later.",
  "error.auth.invalid_email": "Enter a valid email address.",
  "error.auth.code_invalid": "That code does not match. Check it and try again.",
  "error.auth.code_expired": "This code has expired or was tried too many times. Request a new one.",
  "error.auth.signup_closed": "This address cannot create an account on this server.",
  "error.auth.email_unverified":
    "Google has not verified this address, and an account already uses it. Sign in another way and link Google in settings.",
  "error.auth.reauth_required": "For safety, sign in again before deleting your account.",
  "error.identity.in_use":
    "This sign-in method belongs to another account. Accounts cannot be merged: delete the other account first.",
  "error.identity.last": "This is your only sign-in method.",
  "error.rate_limited": "Too many attempts. Wait a minute and try again.",
  "error.network": "The server cannot be reached. Check your connection.",
  "error.generic": "Something went wrong. Try again.",
  "settings.identities": "Sign-in methods",
  "settings.identity.google": "Google",
  "settings.identity.email": "Email",
  "settings.identity.unverified": "Address not verified by Google",
  "settings.identity.added": "Added {date}",
  "settings.identity.remove": "Remove",
  "settings.identity.removeLabel": "Remove {name}",
  "settings.identity.linkGoogle": "Link a Google account",
  "settings.identity.addEmail": "Add an email address",
  "settings.identity.linked":
    "{result, select, google {Google account linked.} exists {That Google account is already linked.} other {This Google account belongs to another account. Accounts cannot be merged: delete the other account first.}}",
  "settings.identity.emailAdded": "Address added.",
  "settings.sessions": "Signed-in devices",
  "settings.session.current": "This device",
  "settings.session.agent": "{browser} on {os}",
  "settings.session.unknownDevice": "Unknown browser",
  "settings.session.signedIn":
    "{provider, select, google {Signed in {date} with Google} other {Signed in {date} with email}}",
  "settings.session.lastSeen": "Last active {date}",
  "settings.session.ip": "IP address {ip}",
  "settings.session.revoke": "Sign out",
  "settings.session.revokeLabel": "Sign out {device}",
  "settings.signOut": "Sign out on this device",
  "settings.activity": "Activity",
  "settings.activity.more": "Show more",
  "settings.activity.empty": "No activity yet.",
  "audit.account.created": "Account created",
  "audit.auth.signed_in": "{provider, select, google {Signed in with Google} other {Signed in with email}}",
  "audit.auth.sign_in_failed": "Failed sign-in attempt",
  "audit.auth.code_sent": "Sign-in code sent",
  "audit.session.revoked":
    "{reason, select, sign_out {Signed out} device_replaced {Signed out: signed in again on the same device} refresh_reused {Signed out: reuse of an old sign-in was detected} idle {Signed out after a long time without use} identity_unlinked {Signed out: its sign-in method was removed} other {Signed out from another device}}",
  "audit.identity.linked": "{provider, select, google {Google account linked} other {Email address linked}}",
  "audit.identity.unlinked": "{provider, select, google {Google account removed} other {Email address removed}}",
  "audit.keys.setup": "Keys set up",
  "audit.keys.wrap_added":
    "{method, select, passkey {Passkey added} device {Device remembered} other {Unlock method added}}",
  "audit.keys.wrap_removed":
    "{method, select, passkey {Passkey removed} device {Device forgotten} other {Unlock method removed}}",
  "audit.keys.recovery_rotated": "Recovery key replaced",
  "audit.link.completed": "A device unlocked by linking",
  "audit.chat.created": "Chat created",
  "audit.chat.archived": "Chat archived",
  "audit.chat.unarchived": "Chat returned from the archive",
  "audit.chat.deleted": "Chat deleted",
  "audit.unknown": "Other activity",
  "settings.delete": "Delete account",
  "settings.delete.intro":
    "Deletes your account and everything in it from this server: chats, notes, data, keys and history. This cannot be undone.",
  "settings.delete.button": "Delete account…",
  "settings.delete.dialogTitle": "Delete your account?",
  "settings.delete.confirmLabel": "Type {phrase} to confirm",
  "settings.delete.phrase": "delete",
  "settings.delete.confirm": "Delete forever",
  "settings.delete.reauth": "Sign in again",

  "keys.loading.title": "Unlocking",
  "keys.loading.text": "Loading your keys…",
  "keys.loading.failed": "Your keys could not be loaded. Check your connection and try again.",
  "keys.setup.title": "Set up your keys",
  "keys.setup.intro":
    "Your chats, notes and data are stored encrypted with keys that only your devices hold. The server keeps them locked and cannot open them.",
  "keys.setup.loss":
    "You unlock with a passkey or a recovery key. If you lose all of them, your data is gone for good: nobody, including the server's administrator, can recover it.",
  "keys.setup.server":
    "While you use a chat, the server and your AI provider see its messages. Encryption protects what is stored, not the conversation in progress.",
  "keys.setup.knownWarning":
    "The server says this account has no keys, yet this browser unlocked it before. If you did not delete the account, the server may be misbehaving. New keys will not bring back anything stored under the old ones.",
  "keys.setup.start": "Set up",
  "keys.setup.step": "Step {n} of {total}",
  "keys.setup.passkey":
    "A passkey unlocks with your fingerprint, face or device PIN. Passkeys synced by your password manager unlock your other devices too.",
  "keys.setup.passkeyUnavailable":
    "This browser cannot use a passkey to hold your key. You will unlock with the recovery key; you can add a passkey later on a device that supports it.",
  "keys.setup.skipPasskey": "Skip, use the recovery key only",
  "keys.setup.continue": "Continue",
  "keys.setup.passkeyAdded": "Passkey added.",
  "keys.setup.finish": "Finish setup",
  "keys.passkey.name": "Vault Chat",
  "keys.passkey.create": "Create a passkey",
  "keys.passkey.add": "Add a passkey",
  "keys.recovery.intro":
    "This is your recovery key. Write the words down in order and keep them somewhere safe and offline. Anyone with these words can unlock your data.",
  "keys.recovery.label": "Recovery key",
  "keys.recovery.once": "The words are shown only once.",
  "keys.recovery.written": "I wrote them down",
  "keys.recovery.suggestions": "Matching words",
  "keys.confirm.intro": "To make sure your copy is right, type these words from it.",
  "keys.confirm.word": "Word {n}",
  "keys.confirm.wrongWord": "This word does not match.",
  "keys.confirm.mismatch": "Some words do not match. Check your copy, or go back to the words.",
  "keys.confirm.back": "Show the words again",
  "keys.remember.label": "Remember this device",
  "keys.remember.hint":
    "Unlocks in this browser without a passkey or recovery key until you sign out here. Anyone holding this device while it is signed in can read your data. Do not use on a shared device.",
  "keys.unlock.title": "Unlock",
  "keys.unlock.intro": "You are signed in. Your chats, notes and data stay locked until you unlock them in this tab.",
  "keys.unlock.passkey": "Unlock with a passkey",
  "keys.unlock.recoveryTitle": "Use the recovery key",
  "keys.unlock.recoveryHint": "The 12 words, in order, separated by spaces.",
  "keys.unlock.recovery": "Unlock",
  "keys.link.title": "Use your phone",
  "keys.link.intro": "If your phone is signed in and unlocked, it can unlock this device: scan a code with its camera.",
  "keys.link.show": "Show a code",
  "keys.link.qr": "Code to scan with your phone",
  "keys.link.scan": "Open your phone's camera and point it at the code. The code changes every 2 minutes.",
  "keys.link.hide": "Hide the code",
  "keys.link.failed": "The answer from your phone did not fit this code. Show a new code and scan it again.",
  "keys.linkPage.title": "Unlock another device",
  "keys.linkPage.ask":
    "A device signed in to your account asks to be unlocked. It describes itself as below; whoever controls it can make it say anything.",
  "keys.linkPage.device": "{browser} on {os}",
  "keys.linkPage.unknownDevice": "Unknown browser",
  "keys.linkPage.created": "Code shown {date}",
  "keys.linkPage.warning":
    "Continue only if you just scanned this code yourself, on a screen of your own. Never for a code or link someone sent you. Unlocking gives that device everything in your account.",
  "keys.linkPage.confirm": "Unlock that device",
  "keys.linkPage.cancel": "Cancel",
  "keys.linkPage.done": "The other device is unlocked. You can close this page.",
  "keys.linkPage.cancelled": "Nothing was sent.",
  "keys.linkPage.expired": "This code is no longer valid. Show a new code on the other device and scan it again.",
  "keys.linkPage.used": "This code was already used.",
  "keys.linkPage.home": "Go to chats",
  "keys.linkPage.signedOutTitle": "Sign in on this phone first",
  "keys.linkPage.signedOut":
    "To unlock another device, this phone must be signed in and unlocked. Sign in, then scan the code on the other device again.",
  "keys.linkPage.signIn": "Sign in",
  "keys.error.wrong": "This does not unlock your account. Check the words, or try another way.",
  "keys.error.length": "The recovery key has 12 words.",
  "keys.error.word": "Word {n} is not a recovery key word. Check its spelling.",
  "keys.error.checksum": "These words are not a valid recovery key. Check their order and spelling.",
  "keys.error.exists": "Keys were just set up on another device. Unlock with them instead.",
  "keys.error.limit": "This account already has the most passkeys allowed. Remove one first.",
  "keys.error.device": "This device could not be remembered. Sign out and sign in again, then retry.",
  "keys.error.passkeyCancelled": "The passkey prompt was closed. Try again when you are ready.",
  "keys.error.noPrf":
    "This passkey cannot hold a key. Use the recovery key, or a passkey from a password manager or device that supports it.",
  "keys.error.passkeyFailed": "The passkey did not work in this browser.",
  "providers.title": "AI providers",
  "providers.locked": "Unlock to manage AI providers.",
  "providers.loadFailed": "Could not read your provider settings. Reload the page to try again.",
  "providers.saveFailed": "Could not save the change. Try again.",
  "providers.intro":
    "Chats use the provider you choose here. Its settings, API key included, are stored encrypted with your other data. While this app is open and unlocked, and for two minutes after, the server holds them in memory to call the provider; a reply still being written keeps them until it ends, for at most 10 minutes. The provider sees what you send it.",
  "providers.empty": "No providers yet. Add one to start chatting.",
  "providers.noneActive": "Choose which provider chats use.",
  "providers.agent.refused":
    "The server refused this provider. Check the address and the model; an address on a private network works only if the server's operator allows it.",
  "providers.agent.failed":
    "Could not start the assistant with this provider. It tries again when the connection comes back.",
  "providers.list": "Providers",
  "providers.active": "In use",
  "providers.kind.openai_compatible": "OpenAI-compatible API",
  "providers.kind.anthropic": "Anthropic API",
  "providers.use": "Use",
  "providers.useLabel": "Use {name}",
  "providers.edit": "Edit",
  "providers.editLabel": "Edit {name}",
  "providers.remove": "Remove",
  "providers.removeLabel": "Remove {name}",
  "providers.removeTitle": "Remove {name}?",
  "providers.removeIntro":
    "It is removed from your settings, API key included, and the server stops using it. Replies it is still writing stop too, unless the connection drops at that moment: then they may run for up to 10 more minutes. Chats keep their messages. The key itself stays valid: the server and the provider have seen it, so if it may have leaked or you no longer need it, revoke it in your account with the provider too.",
  "providers.share": "Share",
  "providers.shareLabel": "Share {name}",
  "providers.share.title": "Share {name}",
  "providers.share.intro":
    "Give this provider to another person, or to a device on another server, as a QR code or a link. Your own devices already have it.",
  "providers.share.warningKey":
    "The code and the link hold the API key in the clear. Anyone who sees the code, has a photo of it or gets the link can use the key and spend on your account at the provider. The phone that scans it may keep the link in its browser history, and a copied link stays on the clipboard.",
  "providers.share.warningNoKey":
    "The code and the link hold the provider's address and model, without the API key. Whoever adds it enters their own key.",
  "providers.share.withKey": "Include the API key",
  "providers.share.withKeyHint": "Leave it out when the other person has a key of their own.",
  "providers.share.show": "Show the QR code",
  "providers.share.qr": "Code with the provider's settings",
  "providers.share.scan": "Scan it with the camera of the other phone and open the link.",
  "providers.share.copy": "Copy the link",
  "providers.share.copied": "Link copied.",
  "providers.share.copyFailed": "Could not copy the link.",
  "providers.form.link": "Shared link",
  "providers.form.linkHint": "Paste a provider link someone gave you, from any server, to fill in the fields.",
  "providers.form.linkFilled": "Filled in from the link. Check the fields before saving.",
  "providers.form.linkInvalid": "This is not a provider link, or it is damaged.",
  "providers.import.title": "Add a shared provider",
  "providers.import.warning":
    "Every chat that uses this provider sends its messages to {host}. Add it only if you know who gave you this link and trust that address.",
  "providers.import.withKey": "With an API key",
  "providers.import.noKey": "Without an API key: add yours in settings",
  "providers.import.add": "Add the provider",
  "providers.import.added": "{name} is added. Choose it in settings to use it in chats.",
  "providers.import.addedActive": "{name} is added, and chats use it.",
  "providers.import.known": "You already have this provider as {name}.",
  "providers.import.cancelled": "Nothing was added.",
  "providers.import.invalid": "This link is damaged or comes from a newer version of the app.",
  "providers.import.missing": "There is no provider to add. Open the link again.",
  "providers.import.settings": "Go to settings",
  "providers.import.signedOutTitle": "Sign in first",
  "providers.import.signedOut":
    "Sign in on this device, then open the link or scan the code again. It is not kept while you sign in, as it may hold an API key.",
  "providers.add": "Add a provider",
  "providers.form.addTitle": "Add a provider",
  "providers.form.editTitle": "Edit provider",
  "providers.form.preset": "Preset",
  "providers.form.presetOther": "Other",
  "providers.form.name": "Name",
  "providers.form.kind": "API",
  "providers.form.baseUrl": "Base URL",
  "providers.form.baseUrlHint": "The server connects to this address, not your device.",
  "providers.form.badUrl": "Enter an address that starts with https:// or http://.",
  "providers.form.apiKey": "API key",
  "providers.form.apiKeyHint": "Leave empty if the provider needs none, as Ollama does.",
  "providers.form.apiKeyEditHint":
    "A key replaced here stays valid at the provider: revoke the old one there. Leave empty if the provider needs none.",
  "providers.form.model": "Model",
  "providers.form.modelHint": "The model's name exactly as the provider lists it.",
  "providers.form.required": "Required.",

  "keys.settings.title": "Unlocking",
  "keys.settings.locked": "Unlock to manage your passkeys and recovery key.",
  "keys.settings.methods": "Unlock methods",
  "keys.method.passkey": "Passkey",
  "keys.method.recovery": "Recovery key",
  "keys.method.device": "This device",
  "keys.method.added": "Added {date}",
  "keys.method.remove": "Remove",
  "keys.method.removeLabel": "Remove the passkey added {date}",
  "keys.rotate.button": "Replace…",
  "keys.rotate.title": "Replace the recovery key",
  "keys.rotate.intro":
    "You get new words. Once you confirm them, the old words stop working. Your passkeys and remembered devices keep working.",
  "keys.rotate.start": "Show new words",
  "keys.rotate.finish": "Replace the recovery key",
  "keys.rotate.done": "The recovery key was replaced. The old words no longer work.",

  "connection.connecting": "Connecting…",
  "connection.waiting": "Connection lost. Retrying in {seconds, plural, one {# second} other {# seconds}}…",
  "connection.revoked": "This session was signed out on another device.",
  "connection.outdated": "A new version is available. Reload the page.",

  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.retry": "Try again",

  "uiKit.notes": "Notes",
  "uiKit.files": "Files",
  "uiKit.sample.photo": "beach.jpg",
  "uiKit.sample.report": "report.pdf",
  "uiKit.sample.table": "steps.csv",
  "uiKit.sample.photoSize": "1.2 MB",
  "uiKit.sample.reportSize": "340 kB",
  "uiKit.sample.photoAbout": "A sandy beach at sunset with two people walking by the water.",
  "uiKit.chats": "Chats",
  "uiKit.sample.chatTitle": "Trip to the sea",
  "uiKit.sample.chatTitle2": "Weekly review",
  "uiKit.sample.question": "Which beaches are quiet in September?\nWe travel with a dog.",
  "uiKit.sample.answer": "Here are three that allow dogs and are calm after the season.",
  "uiKit.sample.rich.heading": "Three quiet beaches",
  "uiKit.sample.rich.bold": "Dogs are welcome",
  "uiKit.sample.rich.italic": "off season",
  "uiKit.sample.rich.struck": "crowded",
  "uiKit.sample.rich.link": "the coast guide",
  "uiKit.sample.rich.beach1": "Golden Cove",
  "uiKit.sample.rich.beach2": "Pine Bay",
  "uiKit.sample.rich.task1": "Book the room",
  "uiKit.sample.rich.task2": "Pack the dog's bowl",
  "uiKit.sample.rich.quote": "Quiet in the mornings, windy after four.",
  "uiKit.sample.rich.callout": "Check the tide table before you go.",
  "uiKit.sample.rich.panel": "How these were chosen",
  "uiKit.sample.rich.beach": "Beach",
  "uiKit.sample.rich.distance": "Distance, km",
  "uiKit.sample.rich.dogs": "Dogs",
  "uiKit.sample.rich.water": "Water temperature, °C",
  "uiKit.sample.rich.month": "Month",
  "uiKit.sample.rich.trips": "Trips by beach",
  "uiKit.sample.rich.other": "Other",
  "uiKit.sample.reason": "To see how your weight changed this month.",
  "uiKit.sample.toolRead": "Weight, 1 Mar 2026 – 31 Mar 2026",
  "uiKit.sample.time": "yesterday",
  "uiKit.sample.toast": "Show a toast",
  "uiKit.title": "UI kit",
  "uiKit.intro": "Every component and variant, for review in every theme and language.",
  "uiKit.buttons": "Buttons",
  "uiKit.fields": "Fields",
  "uiKit.feedback": "Feedback",
  "uiKit.list": "List",
  "uiKit.sample.listTitle": "Firefox on Linux",
  "uiKit.sample.listDetail": "Last active yesterday",
  "uiKit.sample.listDetail2": "IP address 203.0.113.7",
  "uiKit.typography": "Typography",
  "uiKit.dialog": "Dialog",
  "uiKit.sample.primary": "Primary",
  "uiKit.sample.secondary": "Secondary",
  "uiKit.sample.danger": "Danger",
  "uiKit.sample.ghost": "Ghost",
  "uiKit.sample.disabled": "Disabled",
  "uiKit.sample.field": "Display name",
  "uiKit.sample.fieldHint": "Shown to you only.",
  "uiKit.sample.checkbox": "Remember my choice",
  "uiKit.sample.fieldError": "Enter a name.",
  "uiKit.sample.info": "Your data is encrypted with keys only your devices hold.",
  "uiKit.sample.success": "Saved.",
  "uiKit.sample.warning": "Write down your recovery key. It is shown only once.",
  "uiKit.sample.danger.text": "Losing every key means losing your data.",
  "uiKit.crypto": "Crypto worker",
  "uiKit.crypto.run": "Run self-test",
  "uiKit.crypto.ok": "Self-test passed: keys created, record sealed and opened in the worker.",
  "uiKit.crypto.failed": "Self-test failed: {error}",
  "uiKit.sample.openDialog": "Open dialog",
  "uiKit.sample.dialogTitle": "Allow access?",
  "uiKit.sample.dialogBody": "The assistant asks to read your steps for the last 7 days.",
  "uiKit.sample.heading": "The quick brown fox",
  "uiKit.sample.body": "Body text sets the reading rhythm of the app. It wraps at a comfortable measure.",
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
